import { Button } from '@petsmart-ui/sparky';
import { useSearchParams } from 'next/navigation';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useInstantSearch, useStats } from 'react-instantsearch';

import { FILTERS, getNumerFromstring } from '@libs/algolia';
import { dataLayerPush, trackApplyFacetedFilters, trackPLPFilters } from '@libs/analytics';
import { clsx } from '@libs/common-next';
import { currencyFormat } from '@libs/core';

import type { PriceRangeType } from '@/components/PriceRange';
import { INT_RADIX } from '@/constants/numeric';
import { usePriceRangeContext } from '@/providers/PriceRangeProvider';
import { closeButtonDropdown } from '@/utils/closeButtonDropdown';

import '@/components/PriceRange/PriceRange.scss';

// must exceed the 200ms onStateChange debounce in SearchLayout so a real search starts (status != idle) first
const ANALYTICS_FALLBACK_MS = 600;

export default function PriceRange({ priceRange, showClear, showClearTop, showView, pageId }: PriceRangeType) {
    const { range, refine, canRefine } = priceRange;
    const { minValue, maxValue, hasChanged, rangeIsLoaded, setMinValue, setMaxValue, clearRange, pendingAnalytics } =
        usePriceRangeContext();
    const searchParams = useSearchParams();
    const { nbHits } = useStats();
    const { status, results } = useInstantSearch();
    const analyticsTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
    const latest = useRef({ nbHits, status });
    latest.current = { nbHits, status };
    const sliderTrack = useRef<HTMLDivElement>(null);

    const fillColor = useCallback(() => {
        const bgStart = (minValue / FILTERS.RANGE.MAX_VALUE) * 100;
        const bgEnd = (maxValue / FILTERS.RANGE.MAX_VALUE) * 100;
        if (sliderTrack.current) {
            sliderTrack.current.style.background = `
                linear-gradient(to right,
                    var(--sparky-color-neutral-400) ${bgStart}%,
                    var(--sparky-color-brand-blue-500) ${bgStart}%,
                    var(--sparky-color-brand-blue-500) ${bgEnd}%,
                    var(--sparky-color-neutral-400) ${bgEnd}%
                )`;
        }
    }, [minValue, maxValue, sliderTrack]);

    const flushAnalytics = useCallback(() => {
        // pendingAnalytics is shared between all mounted PriceRange instances (toolbar, drawer),
        // so the selected range must live there too; otherwise an untouched instance reports its defaults.
        const pending = pendingAnalytics.current;
        if (!pending) return;
        clearTimeout(analyticsTimer.current);

        const maxValueLog = `${currencyFormat(pending.max)}${pending.max === FILTERS.RANGE.MAX_VALUE ? '+' : ''}`;
        const title = FILTERS.RANGE.TITLE;
        const value = [currencyFormat(pending.min), maxValueLog].join(' - ');
        dataLayerPush({
            event: FILTERS.EVENT.VALUE,
            filter_type: title,
            filter_selection: value,
            results: latest.current.nbHits,
        });
        trackPLPFilters(title, value);
        trackApplyFacetedFilters(title, value, pageId);
        pendingAnalytics.current = null;
    }, [pageId, pendingAnalytics]);

    const viewResults = useCallback(
        (min: number, max: number, shouldLogAnalytics = true) => {
            const rangeMin = range.min as number;
            const rangeMax = range.max as number;

            const refineMinRange = min > rangeMin ? min : rangeMin;
            const refineMaxRange =
                max === FILTERS.RANGE.MAX_VALUE && rangeMax > max ? rangeMax : rangeMax < max ? rangeMax : max;

            if (canRefine) {
                refine([refineMinRange, refineMaxRange]);
            }
            fillColor();
            if (shouldLogAnalytics) {
                pendingAnalytics.current = { min, max };
                // Algolia ignores no-op refinements (values clamped to the catalog range, state deduped in
                // onStateChange), so no search/results may follow: report anyway once the search is idle.
                clearTimeout(analyticsTimer.current);
                analyticsTimer.current = setTimeout(() => {
                    if (latest.current.status === 'idle') flushAnalytics();
                }, ANALYTICS_FALLBACK_MS);
            }
        },
        [fillColor, canRefine, refine, range, pendingAnalytics, flushAnalytics]
    );

    useEffect(() => {
        if (!pendingAnalytics.current) return;
        if (status === 'error') pendingAnalytics.current = null;
        else if (status === 'idle') flushAnalytics();
        // results changes on every search, even when status/nbHits end up identical (cache, same hit count)
    }, [nbHits, status, results, flushAnalytics, pendingAnalytics]);

    const handleMinValue = (e: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, INT_RADIX);
        const currentMinValue = maxValue - value <= 0 ? maxValue : value;

        setMinValue(currentMinValue);
        viewResults(currentMinValue, maxValue);
    };

    const handleMaxValue = (e: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, INT_RADIX);
        const currentMaxValue = value - minValue <= 0 ? minValue : value;
        setMaxValue(currentMaxValue);
        viewResults(minValue, currentMaxValue);
    };

    const clearClasses = clsx(
        showClearTop && ['sparky-c-button', 'sparky-c-button--link', 'price-range__clear-drawer-button'],
        !showClearTop && ['price-range__actions--clear-button', 'has-border']
    );

    const setMinMaxValues = (min: number, max: number, shouldLogAnalytics = true) => {
        if (min && max) {
            setMinValue(min);
            setMaxValue(max);
        } else if (min) {
            setMinValue(min);
        } else if (max) {
            setMaxValue(max);
        }
        viewResults(min, max, shouldLogAnalytics);
    };

    const getMinValueParam = (min: string) => {
        const minValueParam = getNumerFromstring(min) || FILTERS.RANGE.MIN.MIN_VALUE;
        return minValueParam < FILTERS.RANGE.MIN.MIN_VALUE || minValueParam > FILTERS.RANGE.MIN.MAX_VALUE
            ? FILTERS.RANGE.MIN.MIN_VALUE
            : minValueParam;
    };

    const getMaxValueParam = (max: string) => {
        const maxValueParam = getNumerFromstring(max) || FILTERS.RANGE.MAX.MAX_VALUE;
        return maxValueParam < FILTERS.RANGE.MAX.MIN_VALUE || maxValueParam > FILTERS.RANGE.MAX.MAX_VALUE
            ? FILTERS.RANGE.MAX.MAX_VALUE
            : maxValueParam;
    };

    const setValueParams = (minValueParam: number, maxValueParam: number) => {
        if (minValueParam <= maxValueParam) {
            setMinMaxValues(minValueParam, maxValueParam, false);
        } else {
            setMinMaxValues(FILTERS.RANGE.MIN_VALUE, FILTERS.RANGE.MAX_VALUE, false);
        }
    };

    const getUrlValues = () => {
        // need to account for both possible url formats (Velocity or Legacy)
        let [min, max] = '';
        if (searchParams?.get('price')) {
            [min, max] = searchParams.get('price')?.split(':') as unknown as string[];
        }

        if (searchParams?.get('pmin') && searchParams.get('pmin') !== '0.01') {
            min = searchParams.get('pmin') as string;
        }

        if (searchParams?.get('pmax')) {
            max = searchParams.get('pmax') as string;
        }

        return [min !== '' ? parseInt(min, 10).toString() : min, max !== '' ? parseInt(max, 10).toString() : max];
    };

    useEffect(() => {
        if (rangeIsLoaded && !rangeIsLoaded.current) {
            if (minValue !== FILTERS.RANGE.MIN_VALUE || maxValue !== FILTERS.RANGE.MAX_VALUE) {
                rangeIsLoaded.current = true;
            } else {
                rangeIsLoaded.current = false;
            }
        }
        fillColor();
    }, [fillColor, minValue, maxValue, rangeIsLoaded]);

    useEffect(() => {
        if (!searchParams && !hasChanged && rangeIsLoaded?.current) {
            clearRange(() => {
                rangeIsLoaded.current = true;
            });
        }
        const [min, max] = getUrlValues();

        if ((min || max) && rangeIsLoaded && !rangeIsLoaded?.current && !hasChanged) {
            const minValueParam = getMinValueParam(min);
            const maxValueParam = getMaxValueParam(max);
            setValueParams(minValueParam, maxValueParam);
            if (rangeIsLoaded && !rangeIsLoaded.current) {
                rangeIsLoaded.current = true;
            }
        }
    }, []);

    return (
        <div data-testid="price-range" className={clsx('price-range', hasChanged && 'range-slider--selected')}>
            <div className="header-values">
                <div data-testid="price-range-header" className="header-values--range">
                    {`${currencyFormat(minValue)} - ${currencyFormat(
                        maxValue
                    )}${maxValue === FILTERS.RANGE.MAX_VALUE ? '+' : ''}`}
                </div>
            </div>
            <div className="range-values">
                <div data-testid="price-range-slider-track" ref={sliderTrack} className="slider-track" />

                <input
                    suppressHydrationWarning // Supress Warning: extra attributes coming from the server.
                    data-testid="price-range-slider-input-min-value"
                    type="range"
                    name="rangeMinValue"
                    min={FILTERS.RANGE.MIN.MIN_VALUE}
                    max={FILTERS.RANGE.MIN.MAX_VALUE}
                    step={FILTERS.RANGE.MIN_GAP}
                    value={minValue}
                    onChange={handleMinValue}
                    className={clsx(
                        'range-values--input-range',
                        minValue === FILTERS.RANGE.MAX_VALUE && 'range-values--input-range__has-max-range'
                    )}
                />

                <input
                    suppressHydrationWarning // Supress Warning: extra attributes coming from the server.
                    data-testid="price-range-slider-input-max-value"
                    type="range"
                    name="rangeMaxValue"
                    min={FILTERS.RANGE.MAX.MIN_VALUE}
                    max={FILTERS.RANGE.MAX.MAX_VALUE}
                    step={FILTERS.RANGE.MIN_GAP}
                    value={maxValue}
                    onChange={handleMaxValue}
                    className="range-values--input-range"
                />
            </div>
            <div className="footer-values">
                <span>{currencyFormat(FILTERS.RANGE.MIN_VALUE)}</span>
                <span>{currencyFormat(FILTERS.RANGE.MAX_VALUE / 2)}</span>
                <span>{currencyFormat(FILTERS.RANGE.MAX_VALUE)}+</span>
            </div>
            <div className="price-range__actions">
                {showClear && (
                    <Button
                        data-testid="price-range-actions-button-clear"
                        fullWidth
                        onClick={() => clearRange()}
                        text={FILTERS.CLEAR.TITLE}
                        type="button"
                        variant="link"
                        disabled={!hasChanged}
                        className={clearClasses}
                    />
                )}

                {showView && (
                    <Button
                        data-testid="price-range-actions-button-close"
                        fullWidth
                        onClick={closeButtonDropdown}
                        text={FILTERS.VIEW_RESULTS.TITLE}
                        type="button"
                        variant="primary"
                        className="price-range__actions--view"
                    />
                )}
            </div>
        </div>
    );
}
